"use client";
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { PortalScope, usePortalScope } from "@/analytics/PortalScope";
import { useAuthedApi } from "../../api/useAuthedApi";
import type { Department } from "../../api/departmentsApi";

interface Props {
  open: boolean;
  onClose: () => void;
  documentId: number;
  documentLabel: string;
  /** The document is personal (still carries its uploader's user_id). Then a
   *  department promotion MOVES it (decision D6) into the picked departments:
   *  one owns it — its editors and the company admin manage it — and the
   *  others can read it (spec 2026-10-02 §3.2). Otherwise the picker shares a
   *  department/company document read-only with further departments. */
  personal: boolean;
  /** Departments a PERSONAL document may move into — company admins get
   *  every department, everyone else only the ones they belong to (mirrors
   *  KnowledgePage's own upload-target gating). The backend enforces this
   *  the same way (member/editor of the target dept, or admin), so the
   *  picker must not offer departments the move would 403 on. */
  manageableDepts: Department[];
  /** Departments a department/company document may be SHARED with: every
   *  department of the company (decision B — the owning department's editor
   *  shares into any of them; the backend checks the caller may write the
   *  document and that each target is the company's). Falls back to
   *  `manageableDepts` when not given. */
  shareDepts?: Department[];
  /** Refetch the caller's document list — mirrors ShareDocumentDialog's
   *  effect-on-mutation pattern (there it's baked into onGrant/onRevoke;
   *  here the caller owns the list so we hand control back via a callback,
   *  same shape as DocumentRowItem's `onDeleted`). */
  onChanged: () => void;
}

function moveLabel(count: number): string {
  if (count === 0) return "Move to departments";
  return `Move to ${count} department${count === 1 ? "" : "s"}`;
}

function message(e: unknown): string {
  return e instanceof Error && e.message ? e.message : "Something went wrong";
}

/** Move a personal document into one or more departments, share a department document
 *  read-only with further departments, or promote either to the entire
 *  company. Mirrors ShareDocumentDialog's structure (load/mutate/
 *  error-message pattern, busy state) but for tier-wide visibility instead of
 *  per-user sharing grants.
 *
 *  NOTE: this dialog always starts from an empty department selection — there
 *  is no existing GET that returns a document's current `granted_dept_ids`
 *  (only the POST /visibility response carries it, and only after a call).
 *  Adding a new fetch just to prefill this is out of scope here; the dialog is
 *  write-only per visit, which matches how ShareDocumentDialog's own grant
 *  picker works (it always starts unselected too). */
export function PromoteVisibilityDialog({
  open,
  onClose,
  documentId,
  documentLabel,
  personal,
  manageableDepts,
  shareDepts,
  onChanged,
}: Props) {
  const pickable = personal ? manageableDepts : (shareDepts ?? manageableDepts);
  const { documents } = useAuthedApi();
  const [selectedDeptIds, setSelectedDeptIds] = useState<number[]>([]);
  // Only meaningful for a personal document: which picked department owns it.
  const [ownerDeptId, setOwnerDeptId] = useState<number | null>(null);
  const [busyDept, setBusyDept] = useState(false);
  const [busyCompany, setBusyCompany] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelectedDeptIds([]);
    setOwnerDeptId(null);
    setError(null);
  }, [open]);

  const deptName = useCallback(
    (id: number) =>
      pickable.find((d) => d.id === id)?.name ?? `Department ${id}`,
    [pickable],
  );

  const onSelectChange = (e: SelectChangeEvent<number[]>) => {
    const value = e.target.value;
    const next = (typeof value === "string" ? value.split(",") : value).map((v) =>
      Number(v),
    );
    setSelectedDeptIds(next);
    // The first pick owns it by default; unpicking the owner hands ownership
    // to the earliest remaining pick, so there is always exactly one.
    setOwnerDeptId((owner) =>
      owner !== null && next.includes(owner) ? owner : (next[0] ?? null),
    );
  };

  const onPromoteToDepartments = async () => {
    if (selectedDeptIds.length === 0) return;
    setBusyDept(true);
    setError(null);
    try {
      // One pick sends today's body unchanged (the backend makes that entry
      // the owner); several name the owner explicitly.
      await documents.setVisibility(documentId, {
        scope: "department",
        dept_ids: selectedDeptIds,
        ...(personal && selectedDeptIds.length > 1 && ownerDeptId !== null
          ? { owner_dept_id: ownerDeptId }
          : {}),
      });
      onChanged();
      onClose();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusyDept(false);
    }
  };

  const onPromoteToCompany = async () => {
    setBusyCompany(true);
    setError(null);
    try {
      await documents.setVisibility(documentId, { scope: "company" });
      onChanged();
      onClose();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusyCompany(false);
    }
  };

  const busy = busyDept || busyCompany;
  // The picker's menu is a second portal, outside the dialog's own (D7).
  const deptMenuScope = usePortalScope("visibility_dialog");

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <PortalScope section="visibility_dialog">
      <DialogTitle>Change visibility of &quot;{documentLabel}&quot;</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {personal ? (
            <Typography variant="body2" color="text.secondary">
              Moving it into departments makes it a department document: it is no
              longer a personal document, and only the owner department&apos;s editors
              and the company admin can then change or delete it — you too only if
              you are one of its editors.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Give further departments read-only access to this document — or make it
              company-wide.
            </Typography>
          )}

          <FormControl size="small" fullWidth disabled={pickable.length === 0}>
            <InputLabel id="promote-visibility-dept-label">Departments</InputLabel>
            <Select
              labelId="promote-visibility-dept-label"
              label="Departments"
              data-ph="open"
              MenuProps={{ slotProps: { list: deptMenuScope } }}
              multiple
              value={selectedDeptIds}
              onChange={onSelectChange}
              renderValue={(selected) =>
                (selected as number[]).map((id) => deptName(id)).join(", ")
              }
            >
              {pickable.map((d) => (
                <MenuItem key={d.id} value={d.id} data-ph="select">
                  {d.name}
                </MenuItem>
              ))}
            </Select>
            {personal && (
              <FormHelperText>
                The owner department&apos;s editors manage it; the others can read it.
              </FormHelperText>
            )}
          </FormControl>

          {selectedDeptIds.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selectedDeptIds.map((id) =>
                !personal ? (
                  <Chip key={id} size="small" label={deptName(id)} />
                ) : id === ownerDeptId ? (
                  <Chip key={id} size="small" color="primary" label={`${deptName(id)} (owner)`} />
                ) : (
                  <Chip
                    key={id}
                    size="small"
                    variant="outlined"
                    label={deptName(id)}
                    onClick={() => setOwnerDeptId(id)}
                    data-ph="select_owner"
                    aria-label={`Make ${deptName(id)} the owner`}
                  />
                ),
              )}
            </Box>
          )}

          <Button
            variant="contained"
            onClick={onPromoteToDepartments}
            data-ph="assign_departments"
            disabled={busy || selectedDeptIds.length === 0}
            startIcon={busyDept ? <CircularProgress size={16} /> : undefined}
          >
            {personal
              ? moveLabel(selectedDeptIds.length)
              : "Promote to selected department(s)"}
          </Button>

          <Divider textAlign="center">
            <Typography variant="caption">or</Typography>
          </Divider>

          <Button
            variant="outlined"
            onClick={onPromoteToCompany}
            data-ph="assign_company"
            disabled={busy}
            startIcon={busyCompany ? <CircularProgress size={16} /> : undefined}
          >
            Promote to company-wide
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} data-ph="cancel">
          Cancel
        </Button>
      </DialogActions>
      </PortalScope>
    </Dialog>
  );
}
